import { Component, ChangeDetectionStrategy, Inject, OnDestroy } from '@angular/core';
import { MatCalendar } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-month-day-datepicker-header',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="month-day-header">
      <button class="month-day-header__prev" (click)="previousMonth()" type="button">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13 1L14.4 2.5L7.00002 10L14.4 17.5L13 19L4.00002 10L13 1Z" fill="currentColor"/>
        </svg>
      </button>
      <span class="month-day-header__label">{{ monthLabel }}</span>
      <button class="month-day-header__next" (click)="nextMonth()" type="button">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.99998 1L5.59998 2.5L13 10L5.59998 17.5L6.99998 19L16 10L6.99998 1Z" fill="currentColor"/>
        </svg>
      </button>
    </div>
  `,
  styles: [`
    .month-day-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px 8px 16px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    }

    .month-day-header__label {
      font-family: 'Rethink Sans', sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: rgba(0, 0, 0, 0.87);
      letter-spacing: 0.5px;
    }

    .month-day-header__prev,
    .month-day-header__next {
      width: 32px;
      height: 32px;
      border: none;
      background: transparent;
      color: rgba(0, 0, 0, 0.54);
      cursor: pointer;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      transition: all 0.2s ease;
    }

    .month-day-header__prev:hover,
    .month-day-header__next:hover {
      color: rgba(0, 0, 0, 0.87);
      background-color: rgba(0, 0, 0, 0.04);
    }

    .month-day-header__prev svg,
    .month-day-header__next svg {
      display: block;
    }
  `]
})
export class MonthDayDatepickerHeaderComponent<D> {
  private _destroyed = new Subject<void>();

  constructor(
    private _calendar: MatCalendar<D>,
    private _dateAdapter: DateAdapter<D>,
    @Inject(MAT_DATE_FORMATS) private _dateFormats: MatDateFormats,
  ) {
    _calendar.stateChanges
      .pipe(takeUntil(this._destroyed))
      .subscribe(() => {});
  }

  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }

  get monthLabel(): string {
    // Get full month name (e.g., "January", "February")
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthIndex = this._dateAdapter.getMonth(this._calendar.activeDate);
    return monthNames[monthIndex];
  }

  previousMonth(): void {
    const currentDate = this._calendar.activeDate;
    this._calendar.activeDate = this._dateAdapter.addCalendarMonths(currentDate, -1);
  }

  nextMonth(): void {
    const currentDate = this._calendar.activeDate;
    this._calendar.activeDate = this._dateAdapter.addCalendarMonths(currentDate, 1);
  }
}
